/**
 * Pushes first-touch marketing attribution — captured on landing into the
 * `_fct_attribution` cookie by snippets/attribution-capture.liquid — onto the
 * cart as attributes, so it survives to the order for downstream attribution.
 *
 * Listens on `cart:change` (regular add-to-cart / quantity changes),
 * `cart:refresh`, `sample:added` (assets/sample-add.js's <sample-add>,
 * dispatched on `document` after a successful Add Sample — does not reliably
 * surface as `cart:change`), and Ketch's own `consent` event, plus once on
 * init, so every mutation path — and every point consent could newly allow a
 * sync — gets a chance to run one.
 *
 * Must live in the theme, not a Web Pixel: Web Pixels run sandboxed and cannot
 * write cart attributes via the AJAX Cart API.
 *
 * Re-checks consent at every sync, not just at capture time: ketch-consent.liquid
 * bridges Ketch's `behavioral_advertising` purpose into
 * `Shopify.customerPrivacy` on every consent change, so a shopper who revokes
 * consent mid-session stops getting new attribution writes from that point on
 * (existing cart attributes are left as-is — this only gates future writes).
 * Fails closed: if the signal isn't available yet (Ketch still loading), no
 * sync happens for that attempt — the Ketch `consent` listener below is what
 * retries, polling briefly (see waitForConsentBridge) for
 * Shopify.customerPrivacy to actually catch up rather than waiting on the
 * shopper's next cart mutation, which might never come.
 */
(function () {
    'use strict';

    var COOKIE_NAME = '_fct_attribution';
    var ATTRIBUTE_KEYS = [
        'gclid',
        'gbraid',
        'wbraid',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'ga_client_id',
    ];
    // Shopify hides any cart/line-item property whose name starts with `_` from
    // the cart, checkout, and order UI (customer-facing but not vendor-enforced —
    // Shopify's own docs describe it, nothing rejects an unprefixed name). Matches
    // the existing `_is_trade_account` attribute in cart-trade-account-manager.js.
    var CART_ATTRIBUTE_PREFIX = '_';

    function readCookie(name) {
        var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    }

    function storedAttribution() {
        var raw = readCookie(COOKIE_NAME);
        if (!raw) return null;
        try {
            var parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    function consentAllows() {
        try {
            return !!(
                window.Shopify &&
                window.Shopify.customerPrivacy &&
                window.Shopify.customerPrivacy.saleOfDataAllowed()
            );
        } catch (e) {
            return false;
        }
    }

    // Same resolution attribution-capture.liquid uses: Ketch's raw `consent.purposes`
    // is keyed by this merchant's own Ketch purpose codes, not by the canonical name
    // 'behavioral_advertising', so it has to be cross-referenced against
    // `getConfig()`'s `purposeConfig[].canonicalPurposeCodes` — the same resolution
    // ketch-consent.liquid does before it bridges anything into
    // Shopify.customerPrivacy. Used only to decide whether a consent-change retry is
    // worth pursuing at all; `consentAllows()` (reading the bridged Shopify signal)
    // remains the actual gate inside `syncFromCart`.
    function grantsBehavioralAdvertising(consent, purposeConfig) {
        var granted = (consent && consent.purposes) || {};
        return (purposeConfig || []).some(function (purpose) {
            return (
                granted[purpose.code] &&
                purpose.canonicalPurposeCodes &&
                purpose.canonicalPurposeCodes.indexOf('behavioral_advertising') !== -1
            );
        });
    }

    var CONSENT_BRIDGE_POLL_MS = 50;
    var CONSENT_BRIDGE_TIMEOUT_MS = 2000;

    // Ketch's 'consent' event confirming behavioral advertising is granted does not
    // mean Shopify.customerPrivacy reflects that yet: ketch-consent.liquid's own
    // handler for that SAME event still has to run its own async getConfig() call
    // and then Shopify.customerPrivacy.setTrackingConsent() before consentAllows()
    // actually flips. Calling fetchAndSync() immediately risks the exact race
    // consent-callback-guard.liquid exists to patch for a different caller — the
    // sync would read stale state, fail closed, and (with no cart event to follow)
    // never get retried. Polling for the bridge to actually catch up — same
    // 50ms/2000ms budget consent-callback-guard.liquid uses — is what makes a single
    // consent event with zero subsequent cart activity still apply attribution.
    // Timing out is a silent no-op, not a rethrow: a later cart:change/cart:refresh/
    // sample:added still retries, and this is only ever a best-effort accelerator.
    function waitForConsentBridge(onReady) {
        var waited = 0;

        (function poll() {
            if (consentAllows()) {
                onReady();
                return;
            }
            if (waited >= CONSENT_BRIDGE_TIMEOUT_MS) {
                return;
            }
            waited += CONSENT_BRIDGE_POLL_MS;
            setTimeout(poll, CONSENT_BRIDGE_POLL_MS);
        })();
    }

    function cartRoute() {
        return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
    }

    function cartFetch(url, body) {
        return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }).then(function (res) {
            if (!res.ok) {
                return res.text().then(function (t) {
                    throw new Error('Cart request failed: ' + res.status + ' ' + t);
                });
            }
            return res.json();
        });
    }

    function desiredAttributes(stored) {
        var attributes = {};
        ATTRIBUTE_KEYS.forEach(function (key) {
            if (stored[key]) attributes[CART_ATTRIBUTE_PREFIX + key] = stored[key];
        });
        return attributes;
    }

    function needsSync(cart, desired) {
        var current = (cart && cart.attributes) || {};
        return Object.keys(desired).some(function (key) {
            return current[key] !== desired[key];
        });
    }

    function syncFromCart(cart) {
        if (!consentAllows()) return Promise.resolve(cart);

        var stored = storedAttribution();
        if (!stored) return Promise.resolve(cart);

        var desired = desiredAttributes(stored);
        if (Object.keys(desired).length === 0 || !needsSync(cart, desired)) {
            return Promise.resolve(cart);
        }

        return cartFetch(cartRoute() + 'cart/update.js', { attributes: desired }).catch(function (err) {
            console.error('[cart-attribution-sync] sync:', err);
        });
    }

    function fetchAndSync() {
        return fetch(cartRoute() + 'cart.js')
            .then(function (r) {
                return r.json();
            })
            .then(syncFromCart)
            .catch(function (err) {
                console.error('[cart-attribution-sync] fetch:', err);
            });
    }

    function init() {
        fetchAndSync();

        document.addEventListener(
            'cart:change',
            function (event) {
                var cart = event.detail && event.detail.cart;
                if (cart) {
                    syncFromCart(cart);
                } else {
                    fetchAndSync();
                }
            },
            false,
        );

        document.addEventListener('cart:refresh', fetchAndSync, false);

        // sample-add.js's <sample-add> dispatches this on `document` after a
        // successful sample add, with no `detail.cart` — an Add Sample does not
        // reliably surface as `cart:change`. fetchAndSync() re-reads /cart.js fresh
        // rather than relying on a payload this event doesn't carry.
        document.addEventListener('sample:added', fetchAndSync, false);

        // The startup fetchAndSync() above can run before Ketch has resolved
        // consent, in which case `consentAllows()` fails closed and that attempt
        // is silently skipped — otherwise it would only get retried on the
        // shopper's next cart mutation. Subscribing directly to Ketch's own
        // 'consent' event (the signal that drives the Shopify.customerPrivacy
        // bridge in ketch-consent.liquid) catches consent resolving — or later
        // changing — without waiting on that. Only retries when the resolved
        // purposes actually grant behavioral advertising, so a still-declined
        // shopper doesn't cost a network round trip that would just no-op.
        if (window.ketch) {
            window.ketch('on', 'consent', function (consent) {
                window.ketch('getConfig', function (config) {
                    if (grantsBehavioralAdvertising(consent, config && config.purposes)) {
                        waitForConsentBridge(fetchAndSync);
                    }
                });
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
